import { HttpErrorResponse } from '@angular/common/http';

/**
 * Normalize JSON error bodies (FastAPI `detail` string or list, Spring-style `message`, etc.).
 */
function messageFromParsedBody(body: Record<string, unknown>): string | null {
  if (typeof body['message'] === 'string') {
    return body['message'];
  }
  const detail = body['detail'];
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const o = first as Record<string, unknown>;
      if (typeof o['msg'] === 'string') {
        return o['msg'];
      }
    }
  }
  if (typeof body['error'] === 'string') {
    return body['error'];
  }
  return null;
}

function tryParseBody(raw: unknown): Record<string, unknown> | null {
  if (!raw || raw === null) {
    return null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s.startsWith('{') && !s.startsWith('[')) {
      return null;
    }
    try {
      const parsed = JSON.parse(s) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function isGenericHttpFailureMessage(m: string): boolean {
  return m.startsWith('Http failure response');
}

/**
 * User-facing message from HttpClient errors (structured auth: code/message, or legacy detail/error).
 * Handles backends that return `{ "detail": "..." }` even when Angular leaves `error` as a JSON string.
 */
export function getApiErrorMessage(err: unknown): string {
  if (err === null || err === undefined) {
    return 'Unknown error';
  }

  if (err instanceof HttpErrorResponse) {
    const parsed = tryParseBody(err.error);
    const apiMsg = parsed ? messageFromParsedBody(parsed) : null;
    if (apiMsg) {
      return apiMsg;
    }
    if (typeof err.message === 'string' && err.message !== '' && !isGenericHttpFailureMessage(err.message)) {
      return err.message;
    }
    return typeof err.message === 'string' ? err.message : 'Unknown error';
  }

  const e = err as { error?: unknown; message?: string };
  const fallbackParsed = tryParseBody(e.error);
  const fromApi = fallbackParsed ? messageFromParsedBody(fallbackParsed) : null;
  if (fromApi) {
    return fromApi;
  }
  if (typeof e.message === 'string') {
    return e.message;
  }
  return 'Unknown error';
}
